/**
 * playbackTrace — observability for card-reveal vs audio timing.
 *
 * Two sinks, independent:
 *   1. DB reporting (default ON once configured): every event is batched and
 *      POSTed to `/api/playback/trace`, so card-display timing is persisted
 *      server-side and queryable via SQL / the DB observability tools — not just
 *      visible in a browser console. Configured by the player on mount via
 *      `configurePbTrace({ campaignId, userId, turn, post })`.
 *   2. Verbose console + window buffer (debug-gated): when `?pbtrace=1` or
 *      `localStorage.pbtrace==='1'`, each event is also logged as
 *      `[PBTRACE] <event> {json} @<ms>` and pushed to `window.__pbTrace`
 *      (read via `window.__pbTraceDump()`).
 *
 * Events: audio_play | request_complete | reveal | reveal_trigger | card_completed.
 * The reveal-vs-audio offset for a card = its `reveal` ts minus its `audio_play` ts.
 */

let _verbose = null;
const MAX = 2000;

// ---- DB reporting config (set by configurePbTrace) ----
let _cfg = null;           // { campaignId, userId, getTurn, post }
let _batch = [];
let _timer = null;
const FLUSH_MS = 2500;
const MAX_BATCH = 50;

function verbose() {
  if (_verbose !== null) return _verbose;
  if (typeof window === 'undefined') { _verbose = false; return false; }
  try {
    const q = new URLSearchParams(window.location.search).get('pbtrace');
    _verbose = q === '1' || window.localStorage.getItem('pbtrace') === '1';
  } catch {
    _verbose = false;
  }
  if (_verbose) {
    if (!Array.isArray(window.__pbTrace)) window.__pbTrace = [];
    if (typeof window.__pbTraceDump !== 'function') {
      window.__pbTraceDump = () => {
        const out = window.__pbTrace.slice();
        window.__pbTrace.length = 0;
        return out;
      };
    }
  }
  return _verbose;
}

function scheduleFlush() {
  if (_timer || !_cfg) return;
  try {
    _timer = setTimeout(() => {
      _timer = null;
      flushPbTrace();
    }, FLUSH_MS);
  } catch {
    _timer = null;
  }
}

/**
 * Configure DB reporting. Called by the player on mount; safe to call repeatedly.
 * @param {object} cfg
 * @param {string} cfg.campaignId
 * @param {string} [cfg.userId]
 * @param {() => (number|null)} [cfg.getTurn] - returns the current turn number
 * @param {(payload: object) => void} cfg.post - fire-and-forget POST of the batch body
 */
export function configurePbTrace(cfg) {
  if (!cfg || !cfg.campaignId || typeof cfg.post !== 'function') return;
  _cfg = cfg;
}

/** Stop DB reporting + flush remaining (call on unmount). */
export function teardownPbTrace() {
  flushPbTrace();
  _cfg = null;
}

/** Flush the buffered events to the backend (fire-and-forget). */
export function flushPbTrace() {
  if (!_cfg || _batch.length === 0) return;
  const events = _batch;
  _batch = [];
  try {
    _cfg.post({ campaign_id: _cfg.campaignId, user_id: _cfg.userId ?? null, events });
  } catch {
    /* telemetry must never break playback */
  }
}

/**
 * Record a playback-timing event.
 * @param {string} event
 * @param {object} [data] - { cardId, requestId, group, position, ... }
 */
export function pbTrace(event, data = {}) {
  const t = Math.round(typeof performance !== 'undefined' ? performance.now() : Date.now());

  // Sink 1: DB reporting (default on once configured).
  if (_cfg) {
    const {
      cardId = null,
      requestId = null,
      group = null,
      position = null,
      turn = null,
      ...rest
    } = data;
    _batch.push({
      event,
      card_id: cardId,
      request_id: requestId,
      playback_group: group,
      position,
      turn: turn !== null && turn !== undefined && Number.isFinite(Number(turn))
        ? Number(turn)
        : (typeof _cfg.getTurn === 'function' ? _cfg.getTurn() : null),
      client_ts_ms: t,
      meta: rest && Object.keys(rest).length ? rest : null,
    });
    if (_batch.length >= MAX_BATCH) flushPbTrace();
    else scheduleFlush();
  }

  // Sink 2: verbose console + window buffer (debug-gated).
  if (verbose()) {
    const entry = { t, event, ...data };
    try {
      const buf = window.__pbTrace;
      buf.push(entry);
      if (buf.length > MAX) buf.splice(0, buf.length - MAX);
    } catch { /* ignore */ }
    console.log('[PBTRACE]', event, JSON.stringify(data), '@' + t);
  }
}

/** Record a turn-critical boundary and persist it immediately. */
export function pbTraceBoundary(event, data = {}) {
  pbTrace(event, data);
  flushPbTrace();
}

export default pbTrace;
